/**
 * AvatarCacheContext
 * 
 * Provides app-wide avatar caching to prevent unnecessary reloads.
 * 
 * Features:
 * - In-memory cache for current session
 * - localStorage persistence across sessions
 * - Automatic cache invalidation when avatars are updated
 * - Image preloading for faster display
 * - Stale-while-revalidate pattern
 * 
 * Usage:
 *   // Wrap your app
 *   <AvatarCacheProvider>
 *     <App />
 *   </AvatarCacheProvider>
 * 
 *   // Use in components
 *   const { getCachedAvatar, cacheAvatar, invalidateAvatar } = useAvatarCache();
 *   const avatarUrl = getCachedAvatar(userId) || originalUrl;
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const CACHE_KEY = 'avatar_cache_v1';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Avatar cache entry structure
 * @typedef {Object} CacheEntry
 * @property {string} url - The avatar URL
 * @property {number} timestamp - When it was cached
 * @property {boolean} preloaded - Whether the image has been preloaded
 */

/**
 * Load cache from localStorage
 */
function loadCache() {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Filter out expired entries
      const now = Date.now();
      const valid = {};
      Object.entries(parsed).forEach(([key, entry]) => {
        if (entry.timestamp && (now - entry.timestamp) < CACHE_EXPIRY_MS) {
          valid[key] = entry;
        }
      });
      return valid;
    }
  } catch (err) {
    console.warn('Failed to load avatar cache:', err);
  }
  return {};
}

/**
 * Save cache to localStorage
 */
function saveCache(cache) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.warn('Failed to save avatar cache:', err);
  }
}

/**
 * Preload an image into browser cache
 */
function preloadImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      resolve(false);
      return;
    }
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => reject(new Error('Failed to preload image'));
    img.src = url;
  });
}

// Create context
const AvatarCacheContext = createContext(null);

/**
 * Avatar Cache Provider
 * Wrap your app with this to enable avatar caching
 */
export function AvatarCacheProvider({ children }) {
  // In-memory cache (persisted to localStorage)
  const [cache, setCache] = useState(loadCache);
  
  // Track which images are currently being preloaded
  const preloadingRef = useRef(new Set());
  
  // Save to localStorage whenever cache changes
  useEffect(() => {
    saveCache(cache);
  }, [cache]);
  
  /**
   * Get cached avatar URL for a user
   * Returns null if not cached
   */
  const getCachedAvatar = useCallback((userId) => {
    if (!userId) return null;
    const entry = cache[userId];
    if (entry && entry.url) {
      // Check if still valid
      const age = Date.now() - entry.timestamp;
      if (age < CACHE_EXPIRY_MS) {
        return entry.url;
      }
    }
    return null;
  }, [cache]);
  
  /**
   * Cache an avatar URL for a user
   * Also triggers preloading
   */
  const cacheAvatar = useCallback((userId, url) => {
    if (!userId || !url) return;
    
    setCache(prev => ({
      ...prev,
      [userId]: {
        url,
        timestamp: Date.now(),
        preloaded: false
      }
    }));
    
    // Preload the image if not already preloading
    if (!preloadingRef.current.has(userId)) {
      preloadingRef.current.add(userId);
      preloadImage(url)
        .then(() => {
          setCache(prev => ({
            ...prev,
            [userId]: {
              ...prev[userId],
              preloaded: true
            }
          }));
        })
        .catch(() => {
          // Failed to preload, but URL is still cached
        })
        .finally(() => {
          preloadingRef.current.delete(userId);
        });
    }
  }, []);
  
  /**
   * Invalidate cache for a user (call after avatar update)
   */
  const invalidateAvatar = useCallback((userId) => {
    if (!userId) return;
    setCache(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  }, []);
  
  /**
   * Update avatar in cache (call after successful upload)
   * This immediately updates the cache with the new URL
   */
  const updateAvatar = useCallback((userId, newUrl) => {
    if (!userId || !newUrl) return;
    
    // Invalidate old entry
    invalidateAvatar(userId);
    
    // Cache new URL
    cacheAvatar(userId, newUrl);
  }, [invalidateAvatar, cacheAvatar]);
  
  /**
   * Preload avatars for a list of users
   * Useful when loading a conversation or team list
   */
  const preloadAvatars = useCallback((users) => {
    if (!users || !Array.isArray(users)) return;
    
    users.forEach(user => {
      const userId = user.id || user.user_id;
      const avatarUrl = user.avatar_url || user.avatar;
      
      if (userId && avatarUrl) {
        const cached = getCachedAvatar(userId);
        if (!cached) {
          cacheAvatar(userId, avatarUrl);
        }
      }
    });
  }, [getCachedAvatar, cacheAvatar]);
  
  /**
   * Get cache statistics (for debugging)
   */
  const getCacheStats = useCallback(() => {
    const entries = Object.entries(cache);
    return {
      totalEntries: entries.length,
      preloadedCount: entries.filter(([, e]) => e.preloaded).length,
      oldestEntry: entries.reduce((oldest, [, e]) => 
        e.timestamp < oldest ? e.timestamp : oldest, Date.now()),
      newestEntry: entries.reduce((newest, [, e]) => 
        e.timestamp > newest ? e.timestamp : newest, 0)
    };
  }, [cache]);
  
  /**
   * Clear entire cache (for debugging or logout)
   */
  const clearCache = useCallback(() => {
    setCache({});
    localStorage.removeItem(CACHE_KEY);
  }, []);
  
  const value = {
    getCachedAvatar,
    cacheAvatar,
    invalidateAvatar,
    updateAvatar,
    preloadAvatars,
    getCacheStats,
    clearCache
  };
  
  return (
    <AvatarCacheContext.Provider value={value}>
      {children}
    </AvatarCacheContext.Provider>
  );
}

/**
 * Hook to use avatar cache
 */
export function useAvatarCache() {
  const context = useContext(AvatarCacheContext);
  if (!context) {
    // Return no-op functions if not wrapped in provider
    // This allows components to work without the provider
    return {
      getCachedAvatar: () => null,
      cacheAvatar: () => {},
      invalidateAvatar: () => {},
      updateAvatar: () => {},
      preloadAvatars: () => {},
      getCacheStats: () => ({}),
      clearCache: () => {}
    };
  }
  return context;
}

export default AvatarCacheProvider;
